import type { JSX } from 'react';

import { Link } from 'Components/Buttons/Buttons';
import { CollapsibleModal } from 'Components/Modal/CollapsibleModal/CollapsibleModal';
import { Text } from 'Components/Text/Text';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getIssueDetailsUrl } from 'Helpers/URLBuilder/URLBuilder';
import { DashboardType, IssuesExceptionsDashboardType } from 'Models/Dashboards';
import { IssueResponse } from 'Models/Issues';
import { OrganizationResponseBase } from 'Models/Organization';

import styles from './HistoricalIssuesDisplayModal.module.css';

export enum IssueModalDisplayType {
    OPEN,
    CLOSED,
}

export interface HistoricalIssuesDisplayModalProps {
    hideModal: () => void;
    selectedIssue: IssueResponse[];
    date: string;
    modalType: IssueModalDisplayType;
    dashboardType: IssuesExceptionsDashboardType;
    organizations?: OrganizationResponseBase[];
}

/**
 * For a specific month, displays one of the following (based on where the user clicked in the parent chart):
 * * The number of issues opened
 * * The number of issues closed
 * * The total number of issues in the system
 */
export const HistoricalIssuesDisplayModal = (props: HistoricalIssuesDisplayModalProps): JSX.Element => {
    const organizationIdToNameMap: Map<string, string> = new Map(props.organizations?.map((organization) => [organization.id, organization.name]));

    const displayHeaderText = () => {
        switch (props.modalType) {
            case IssueModalDisplayType.CLOSED:
                return 'Closed Issues';
            case IssueModalDisplayType.OPEN:
                return 'Open Issues';
        }
    };

    const displayIssueDate = (issue: IssueResponse) => {
        switch (props.modalType) {
            case IssueModalDisplayType.CLOSED:
                return iso8601ToUsDateShort(issue.closed_timestamp!);
            case IssueModalDisplayType.OPEN:
                return iso8601ToUsDateShort(issue.created_timestamp);
        }
    };

    const sortIssuesByDateType = (issueA: IssueResponse, issueB: IssueResponse): number => {
        if (props.modalType === IssueModalDisplayType.CLOSED) {
            if (issueA.closed_timestamp! > issueB.closed_timestamp!) {
                return 1;
            } else if (issueA.closed_timestamp! < issueB.closed_timestamp!) {
                return -1;
            } else {
                return issueA.title.localeCompare(issueB.title, undefined, { numeric: true, sensitivity: 'base' });
            }
        } else {
            if (issueA.created_timestamp > issueB.created_timestamp) {
                return 1;
            } else if (issueA.created_timestamp < issueB.created_timestamp) {
                return -1;
            } else {
                return issueA.title.localeCompare(issueB.title, undefined, { numeric: true, sensitivity: 'base' });
            }
        }
    };

    const closedIssueListing = props.selectedIssue.sort(sortIssuesByDateType).map((issue) => (
        <div className={styles.detailsContainer} key={issue.created_timestamp}>
            {props.dashboardType !== DashboardType.CONTROL_AUTOMATION && organizationIdToNameMap.size > 0 && (
                <div className={styles.detailsOrganizationName}>
                    <Text color="darkGray" noStyles={true}>
                        {organizationIdToNameMap.get(issue.impacted_vendor!)}
                    </Text>
                </div>
            )}
            <Link to={getIssueDetailsUrl(issue.id, issue.type)} size="sm" openInNewTab>
                {issue.title}
            </Link>
            <div className={styles.detailsDateContainer}>
                <Text color="darkGray" noStyles={true}>
                    {displayIssueDate(issue)}
                </Text>
            </div>
        </div>
    ));

    return <CollapsibleModal hideModal={props.hideModal} displayItems={closedIssueListing} headerText={{ text: displayHeaderText(), secondaryText: `${props.date}` }} />;
};
